import React from "react";
import { Box, Chip } from "@mui/material";

import { nextTriState, TriState, triStateLabel } from "./stagingData";

export interface TriFilterChipProps {
  /** Filter name, e.g. "In Tipalti". */
  label: string;
  /** null = Any, true = Yes, false = No. */
  value: TriState;
  /** Called with the next value in the cycle Any -> Yes -> No -> Any. */
  onChange: (value: TriState) => void;
  disabled?: boolean;
}

/**
 * Three-way filter chip, "In Tipalti: Any / Yes / No". Each click moves to the next
 * value. Yes uses the ok tone, No the err tone, Any stays neutral.
 */
export function TriFilterChip({
  label,
  value,
  onChange,
  disabled,
}: TriFilterChipProps) {
  const state = triStateLabel(value);
  const tone = value === true ? "ok" : value === false ? "err" : null;

  return (
    <Chip
      variant="outlined"
      clickable
      disabled={disabled}
      onClick={() => onChange(nextTriState(value))}
      aria-label={`${label}: ${state}. Select to change.`}
      title="Select to change: Any, Yes, No"
      label={
        <>
          {label}:{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {state}
          </Box>
        </>
      }
      sx={(theme) => {
        const s = tone ? theme.palette.status[tone] : null;
        const colors = {
          color: s ? s.fg : theme.palette.text.primary,
          backgroundColor: s ? s.bg : theme.palette.background.paper,
          borderColor: s ? s.line : theme.palette.divider,
        };
        return {
          height: 28,
          fontSize: "0.78125rem",
          fontWeight: 400,
          ...colors,
          "&.MuiChip-clickable:hover": {
            ...colors,
            borderColor: s ? s.fg : theme.palette.text.disabled,
          },
          "& .MuiChip-label": { px: 3 },
        };
      }}
    />
  );
}

export default TriFilterChip;
